import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  useWebRTC,
  useWebRTCStatsWatcher,
  useStreamStatus,
  useStreamPlayer,
  useWindowFocus,
  useSessionStats,
  useGamepadConnectionState,
  useTurnConfiguration,
  usePointerLock,
} from "../hooks";
import type { StatsMeta, StreamingCallbacks } from "../types";
import styles from "./EonStreamingView.module.css";
import {
  EON_AUDIO_ID,
  EON_KEYBOARD_INPUT_ID,
  EON_VIDEO_ID,
  LoadingPercentageRanges,
  MediaStreamId,
  MediaStreamType,
} from "../constants/webrtc";
import { convertToWebSocketUrl } from "../utils/url";
import { ConnectionStatusPanel } from "./ConnectionStatusPanel";
import {
  VirtualGamepad,
  type GamepadState,
  INACTIVITY_TIMEOUT_MS,
  VirtualGamepadTemplate,
} from "@onmo/gamepad-manager";
import { useMouseSimulation } from "../hooks/useMouseSimulation";
import { MouseSimulationMode } from "../input/MouseSimulator";
import { getWindowResolution } from "../utils/system";

export interface EonStreamingViewMethods {
  startStream: () => Promise<void>;
  startGamepadPolling: () => void;
  stopGamepadPolling: () => void;
  sendKey: (key: string) => void;
  runRumble: (index?: number) => Promise<string | undefined>;
  runRumbleAll: () => string | undefined;
  resetConnection: () => Promise<void>;
  stopStreamStatusPolling: () => void;
  adjustVideoBitrate: (bitrateKbps: number) => void;
  adjustFps: (fps: number) => void;
  toggleMouseSimulation: () => void;
  shouldSimulateMouseEvents: () => boolean;
  isAudioRequireUserGesture: () => boolean;
  setAudioMuted: (muted: boolean) => void;
  reconnectStream: () => Promise<void>;
}

export interface IVirtualGamepadProps {
  template?: VirtualGamepadTemplate;
  onToggleMenu?: () => void;
}

export interface EonStreamingViewProps {
  streamURL: string;
  streamStatusURL: string;
  signalingUrl?: string;
  callbacks?: StreamingCallbacks;
  enableStats?: boolean;
  statsInterval?: number;
  streamStatusInterval?: number;
  debug?: boolean;
  showVirtualGamepad?: boolean;
  virtualGamepadProps?: IVirtualGamepadProps;
  statsMeta?: StatsMeta;
  isTV?: boolean;
  isWebos?: boolean;
  shouldPoll?: boolean;
  isMenuOpen?: boolean;
  ref?: React.ForwardedRef<EonStreamingViewMethods>;
}

export const EonStreamingView = forwardRef<
  EonStreamingViewMethods,
  Omit<EonStreamingViewProps, "ref">
>(
  (
    {
      streamURL,
      streamStatusURL,
      signalingUrl,
      callbacks,
      enableStats = false,
      debug = false,
      statsInterval,
      streamStatusInterval,
      showVirtualGamepad = false,
      virtualGamepadProps,
      statsMeta,
      isTV = false,
      isWebos = false,
      shouldPoll = true,
      isMenuOpen = false,
    },
    ref,
  ) => {
    const [isDataChannelOpen, setIsDataChannelOpen] = useState(false);
    const [serverLatency, setServerLatency] = useState(0);
    const [isStreamLoaded, setStreamLoaded] = useState(false);
    const [isGamepadDimmed, setIsGamepadDimmed] = useState(false);
    const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const resetInactivityTimer = useCallback(() => {
      setIsGamepadDimmed(false);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(
        () => setIsGamepadDimmed(true),
        INACTIVITY_TIMEOUT_MS,
      );
    }, []);

    const {
      videoRef,
      audioRef,
      playStream,
      startStream: startStreamPlayback,
      resumeAudio,
      pauseAudio,
      resumeAudioStream,
      isAudioRequireUserGesture,
    } = useStreamPlayer({
      onPlaybackError: (streamType, error) => {
        if (error.name === "NotAllowedError") {
          console.log(`User interaction required for ${streamType} playback`);
          callbacks?.onPlayStreamRequired?.(streamType);
        }
      },
      onAudioResumed: () => {
        callbacks?.onAudioResumed?.();
      },
    });

    const _signalingUrl =
      signalingUrl || convertToWebSocketUrl(streamURL, "webrtc/signalling/");

    const { streamStatus, stopPolling } = useStreamStatus({
      streamStatusURL,
      pollingInterval: streamStatusInterval,
      onStreamStatus: callbacks?.onStreamStatus,
    });

    const { turnConfiguration } = useTurnConfiguration({
      streamURL,
      streamStatus,
      onError: callbacks?.onError,
    });

    const {
      shouldSimulateMouseEvents,
      simulateMouseMovement,
      simulateMouseEvent,
      stop: stopMouseSimulation,
    } = useMouseSimulation({ streamStatus, mode: MouseSimulationMode.GESTURE });

    const startStream = async () => {
      await startStreamPlayback();
    };

    const startGamepadPolling = () => {
      videoWebRTC.connection?.startGamepadPolling();
    };

    const stopGamepadPolling = () => {
      videoWebRTC.connection?.stopGamepadPolling();
    };

    const onLatencyMeasurement = (latency: number) => {
      setServerLatency(latency);
      callbacks?.onLatencyMeasurement?.(latency);
    };

    const toggleMouseSimulation = () => {
      videoWebRTC.connection?.toggleMouseSimulation();
    };

    const videoWebRTC = useWebRTC({
      callbacks: {
        ...callbacks,
        onStreamReceived: (stream) => {
          playStream(stream, MediaStreamType.Video);
          callbacks?.onStreamReceived?.(stream);
        },
        onDataChannelOpen: () => {
          setIsDataChannelOpen(true);
          callbacks?.onDataChannelOpen?.();
        },
        onDataChannelClose: () => {
          setIsDataChannelOpen(false);
          callbacks?.onDataChannelClose?.();
        },
        onDataChannelError: (error) => {
          callbacks?.onDataChannelError?.(error);
        },
        onLatencyMeasurement,
      },
      videoElement: videoRef.current,
      signalingConfig: {
        url: _signalingUrl,
      },
      mediaStreamId: MediaStreamId.Video,
      turnConfiguration: turnConfiguration || undefined,
      isTV,
    });

    const audioWebRTC = useWebRTC({
      callbacks: {
        onStreamReceived: (stream) => {
          playStream(stream, MediaStreamType.Audio);
          callbacks?.onStreamReceived?.(stream);
        },
        onSystemEvent: callbacks?.onSystemEvent,
        onError: callbacks?.onError,
      },
      signalingConfig: {
        url: _signalingUrl,
      },
      mediaStreamId: MediaStreamId.Audio,
      turnConfiguration: turnConfiguration || undefined,
      audioElement: audioRef.current,
    });

    // Handle window focus events for the video connection
    useWindowFocus({
      connection: videoWebRTC.connection || undefined,
      audioConnection: audioWebRTC.connection || undefined,
      pauseAudio,
      resumeAudioStream,
      isWebos,
      isTV,
    });

    useWebRTCStatsWatcher({
      enabled: enableStats,
      videoConnection: videoWebRTC.connection,
      audioConnection: audioWebRTC.connection,
      serverLatency,
      statsInterval: isTV ? 5000 : statsInterval,
      onStatsUpdate: callbacks?.onStatsUpdate,
      isVideoConnected: videoWebRTC.connectionState.isConnected,
      isAudioConnected: audioWebRTC.connectionState.isConnected,
    });

    useEffect(() => {
      if (videoWebRTC.connection) {
        initializeConnection(MediaStreamType.Video);
      }
    }, [videoWebRTC.connection]);

    useEffect(() => {
      if (audioWebRTC.connection) {
        initializeConnection(MediaStreamType.Audio);
      }
    }, [audioWebRTC.connection]);

    useEffect(() => {
      return () => {
        videoWebRTC?.cleanup();
        audioWebRTC?.cleanup();
      };
    }, []);

    // Pass stream status to input handler for mouse simulation
    useEffect(() => {
      if (videoWebRTC.connection) {
        videoWebRTC.connection.setStreamStatus(streamStatus);
      }
    }, [videoWebRTC.connection, streamStatus]);

    useEffect(() => {
      videoWebRTC.connection?.setShouldPoll(shouldPoll);
    }, [videoWebRTC.connection, shouldPoll]);

    useEffect(() => {
      videoWebRTC.connection?.setMenuOpen(isMenuOpen);
    }, [videoWebRTC.connection, isMenuOpen]);

    const initializeConnection = async (streamType: MediaStreamType) => {
      try {
        const webrtc =
          streamType === MediaStreamType.Video ? videoWebRTC : audioWebRTC;
        webrtc.connect();
      } catch (err) {
        console.error(`Error initializing ${streamType} streaming:`, err);
      }
    };

    useEffect(() => {
      let rafId: number;
      let timeoutId: ReturnType<typeof setTimeout>;

      const measureAndUpdate = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = requestAnimationFrame(() => {
            if (!isStreamLoaded) return;
            const [width, height] = getWindowResolution();

            if (videoRef.current) {
              videoRef.current.style.width = `${
                width / window.devicePixelRatio
              }px`;
              videoRef.current.style.height = `${
                height / window.devicePixelRatio
              }px`;
            }

            // Third rAF: read rect AFTER the style changes above have been painted
            rafId = requestAnimationFrame(() => {
              videoWebRTC.connection?.updateMapping();
            });
          });
        });
      };

      const debouncedUpdate = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(measureAndUpdate, 150);
      };

      // Initial call — defer so browser finishes layout (address bar, status bar, etc.)
      timeoutId = setTimeout(measureAndUpdate, 300);

      window.addEventListener("resize", debouncedUpdate);

      return () => {
        window.removeEventListener("resize", debouncedUpdate);
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId);
      };
    }, [isStreamLoaded]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      startStream,
      startGamepadPolling,
      stopGamepadPolling,
      sendKey: (key: string) => videoWebRTC.connection?.sendKey(key),
      runRumble: (index?: number) =>
        videoWebRTC.connection?.runRumble(index) ?? Promise.resolve(undefined),
      runRumbleAll: () => videoWebRTC.connection?.runRumbleAll(),
      resetConnection: async () => {
        await Promise.all([videoWebRTC.reset(), audioWebRTC.reset()]);
      },
      stopStreamStatusPolling: () => {
        stopPolling();
      },
      toggleMouseSimulation: () => toggleMouseSimulation?.(),
      shouldSimulateMouseEvents: () => {
        const mouseSimulator = videoWebRTC.connection?.getMouseSimulator();
        return mouseSimulator?.shouldSimulateMouseEvents() ?? false;
      },
      resumeAudio,
      pauseAudio,
      resumeAudioStream,
      isAudioRequireUserGesture,
      adjustVideoBitrate: (bitrateKbps: number) => {
        videoWebRTC.connection?.adjustVideoBitrate(bitrateKbps);
      },
      adjustFps: (fps: number) => {
        videoWebRTC.connection?.adjustFps(fps);
      },
      setAudioMuted: (muted: boolean) => {
        if (audioRef.current) audioRef.current.muted = muted;
      },
      reconnectStream: async () => {
        await Promise.all([videoWebRTC.reset(), audioWebRTC.reset()]);
      },
    }));

    const onReconnect = () => {
      videoWebRTC.disconnect();
      audioWebRTC.disconnect();
    };

    const onSimulateDisconnectReconnect = async () => {
      console.log("[Test] Simulating internet disconnect...");

      // 1. Simulate offline: fire callback only (matches real behavior)
      callbacks?.onNetworkDisconnect?.();

      // 2. Wait to simulate network being down
      console.log("[Test] Network is down, waiting...");
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // 3. Simulate online: fire callback + start ICE recovery fallback
      console.log("[Test] Simulating internet reconnect...");
      callbacks?.onNetworkReconnected?.();

      videoWebRTC.reset();
      audioWebRTC.reset();
    };

    usePointerLock(EON_VIDEO_ID, {
      onLocked: startStream,
      isStreamLoaded,
      showVirtualGamepad,
      streamStatus,
      isTV,
    });

    const { isGamepadConnected } = useGamepadConnectionState();

    const isGamepadActive =
      showVirtualGamepad &&
      isDataChannelOpen &&
      isStreamLoaded &&
      !isGamepadConnected;

    useEffect(() => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (isGamepadActive) {
        setIsGamepadDimmed(false);
        inactivityTimerRef.current = setTimeout(
          () => setIsGamepadDimmed(true),
          INACTIVITY_TIMEOUT_MS,
        );
      } else {
        setIsGamepadDimmed(false);
      }
      return () => {
        if (inactivityTimerRef.current)
          clearTimeout(inactivityTimerRef.current);
      };
    }, [isGamepadActive]);

    useSessionStats({
      enabled: statsMeta !== undefined,
      rtcConnection: videoWebRTC.connection,
      statsMeta,
      streamConnected: videoWebRTC.connectionState.isConnected,
    });

    return (
      <div
        className={styles.container}
        onTouchStartCapture={isGamepadActive ? resetInactivityTimer : undefined}
        onTouchMoveCapture={isGamepadActive ? resetInactivityTimer : undefined}
        onMouseDownCapture={isGamepadActive ? resetInactivityTimer : undefined}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={true}
          className={styles.video}
          id={EON_VIDEO_ID}
          style={{ visibility: isStreamLoaded ? "visible" : "hidden" }}
          onPlaying={() => {
            setStreamLoaded(true);
            callbacks?.onLoadingPercentage?.(LoadingPercentageRanges.CONNECTED);
          }}
        />
        <audio
          ref={audioRef}
          id={EON_AUDIO_ID}
          playsInline
          controls={false}
          muted={false}
        />
        <input
          type="text"
          id={EON_KEYBOARD_INPUT_ID}
          className={styles.keyboardInput}
          placeholder="Type here..."
        />
        <VirtualGamepad
          isActive={isGamepadActive}
          dimmed={isGamepadDimmed}
          sendMessage={
            videoWebRTC.connection?.getVirtualGamepadSender() || undefined
          }
          onStateChange={(state: GamepadState) => {
            callbacks?.onVirtualGamepadStateChange?.(state);
          }}
          shouldSimulateMouseEvents={shouldSimulateMouseEvents}
          simulateMouseMovement={simulateMouseMovement}
          simulateMouseEvent={simulateMouseEvent}
          stopMouseSimulation={stopMouseSimulation}
          {...virtualGamepadProps}
        />
        {debug && (
          <ConnectionStatusPanel
            videoSignalingStatus={videoWebRTC.signalingStatus}
            audioSignalingStatus={audioWebRTC.signalingStatus}
            videoWebRTCConnected={videoWebRTC.connectionState.isConnected}
            videoWebRTCState={videoWebRTC.connectionState.connectionState}
            audioWebRTCConnected={audioWebRTC.connectionState.isConnected}
            audioWebRTCState={audioWebRTC.connectionState.connectionState}
            isDataChannelOpen={isDataChannelOpen}
            streamStatus={streamStatus}
            onReconnect={onReconnect}
            onSimulateDisconnectReconnect={onSimulateDisconnectReconnect}
          />
        )}
      </div>
    );
  },
);
